/**
 * B1 (E-1.B.1-01): Migration Wizard Shell
 *
 * 6-step wizard: Connect → Discover → Map → Preview → Execute → Verify
 * - Progress bar with step labels
 * - Back/forward navigation
 * - State persisted in migration_jobs table via API
 * - Admin+ only (enforced by route guard)
 */
import { useState, useCallback, type ReactNode } from "react";
import { useTranslation } from "@/i18n";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Wizard step definitions
// ---------------------------------------------------------------------------

export const WIZARD_STEPS = [
  { key: "connect", labelKey: "migration.steps.connect", icon: "🔗" },
  { key: "discover", labelKey: "migration.steps.discover", icon: "🔍" },
  { key: "map", labelKey: "migration.steps.map", icon: "🗺️" },
  { key: "preview", labelKey: "migration.steps.preview", icon: "👁️" },
  { key: "execute", labelKey: "migration.steps.execute", icon: "🚀" },
  { key: "verify", labelKey: "migration.steps.verify", icon: "✅" },
] as const;

export type WizardStepKey = (typeof WIZARD_STEPS)[number]["key"];

// ---------------------------------------------------------------------------
// Wizard state
// ---------------------------------------------------------------------------

export interface WizardState {
  currentStep: number;
  /** Migration job ID — created on first step completion */
  jobId: string | null;
  /** GHL API key (in-memory only, never persisted client-side) */
  ghlApiKey: string | null;
  /** GHL account info from validation */
  ghlAccount: { name: string; locationCount: number; contactCount: number } | null;
  /** Discovered entities from GHL */
  discoveredEntities: Record<string, { count: number; selected: boolean }> | null;
  /** Field mappings */
  fieldMappings: Array<{ sourceField: string; targetField: string; transformType: string }> | null;
  /** Dry-run results */
  previewResults: unknown[] | null;
  /** Import status */
  importStatus: "idle" | "running" | "completed" | "failed";
}

const INITIAL_STATE: WizardState = {
  currentStep: 0,
  jobId: null,
  ghlApiKey: null,
  ghlAccount: null,
  discoveredEntities: null,
  fieldMappings: null,
  previewResults: null,
  importStatus: "idle",
};

// ---------------------------------------------------------------------------
// Progress bar component
// ---------------------------------------------------------------------------

function ProgressBar({ currentStep }: { currentStep: number }) {
  const { t } = useTranslation();

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {WIZARD_STEPS.map((step, idx) => (
          <div key={step.key} className="flex flex-1 items-center">
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold transition-colors",
                  idx < currentStep
                    ? "bg-[var(--color-success)]/50 text-white"
                    : idx === currentStep
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground",
                )}
              >
                {idx < currentStep ? "✓" : step.icon}
              </div>
              <span
                className={cn(
                  "mt-1.5 text-xs font-medium",
                  idx <= currentStep ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {t(step.labelKey)}
              </span>
            </div>
            {idx < WIZARD_STEPS.length - 1 && (
              <div
                className={cn(
                  "mx-2 h-0.5 flex-1",
                  idx < currentStep ? "bg-[var(--color-success)]/50" : "bg-muted",
                )}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main wizard shell
// ---------------------------------------------------------------------------

interface WizardShellProps {
  /** Render function for each step — receives state + actions */
  children: (props: {
    state: WizardState;
    goNext: () => void;
    goBack: () => void;
    updateState: (patch: Partial<WizardState>) => void;
    canGoNext: boolean;
    canGoBack: boolean;
    isFirstStep: boolean;
    isLastStep: boolean;
  }) => ReactNode;
}

export function WizardShell({ children }: WizardShellProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<WizardState>(INITIAL_STATE);

  const goNext = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, WIZARD_STEPS.length - 1),
    }));
  }, []);

  const goBack = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 0),
    }));
  }, []);

  const updateState = useCallback((patch: Partial<WizardState>) => {
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  const isFirstStep = state.currentStep === 0;
  const isLastStep = state.currentStep === WIZARD_STEPS.length - 1;

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">
          {t("migration.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("migration.subtitle")}
        </p>
      </div>

      <ProgressBar currentStep={state.currentStep} />

      <div className="rounded-lg border border-border bg-card p-6">
        {children({
          state,
          goNext,
          goBack,
          updateState,
          canGoNext: state.currentStep < WIZARD_STEPS.length - 1,
          canGoBack: state.currentStep > 0,
          isFirstStep,
          isLastStep,
        })}
      </div>

      {/* Navigation buttons */}
      <div className="mt-4 flex justify-between">
        <button
          onClick={goBack}
          disabled={isFirstStep}
          className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground disabled:opacity-30"
        >
          {t("common.back")}
        </button>
        {!isLastStep && (
          <button
            onClick={goNext}
            className="rounded-md bg-primary px-6 py-2 text-sm font-semibold text-primary-foreground"
          >
            {t("common.next")}
          </button>
        )}
      </div>
    </div>
  );
}
